import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, LogOut, Camera, CheckCircle, XCircle, AlertCircle, 
  Search, Euro, Users, QrCode, Barcode, Zap, ArrowLeft, Sparkles, 
  User, CreditCard, Gift, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function QuickScanner({ user, onLogout }) {
  const { isDark } = useTheme();
  
  // Theme classes
  const t = {
    bg: isDark ? 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]' : 'bg-gradient-to-br from-[#F9F7F2] via-[#FFFFFF] to-[#F5F3EE]',
    card: isDark ? 'bg-white/5 backdrop-blur-xl border-white/10' : 'bg-white shadow-lg border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSecondary: isDark ? 'text-white/60' : 'text-gray-600',
    textMuted: isDark ? 'text-white/40' : 'text-gray-400',
    input: isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400',
    header: isDark ? 'bg-black/20 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200',
    navLink: isDark ? 'text-white/60 hover:text-white' : 'text-gray-600 hover:text-gray-900',
    glow1: isDark ? 'bg-[#4A5D23]/20' : 'bg-[#4A5D23]/5',
    glow2: isDark ? 'bg-[#C5A059]/10' : 'bg-[#C5A059]/5',
  };
  
  const [mode, setMode] = useState('select');
  const [scannedCardId, setScannedCardId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCard, setCustomerCard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [amount, setAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const scannerRef = useRef(null);
  const amountInputRef = useRef(null);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, getAuthHeaders());
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  useEffect(() => {
    if (mode === 'qr' && !scannerRef.current) {
      const scanner = new Html5QrcodeScanner(
        "qr-scanner-quick",
        { 
          fps: 15, 
          qrbox: { width: 250, height: 250 },
          rememberLastUsedCamera: true,
          aspectRatio: 1.0,
          showTorchButtonIfSupported: true,
          formatsToSupport: [ 0 ],
        },
        false
      );
      scanner.render(onScanSuccess, onScanFailure);
      scannerRef.current = scanner;
    }
    return () => {
      if (scannerRef.current && mode !== 'qr') {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
      }
    };
  }, [mode]);

  useEffect(() => {
    if (mode === 'amount' && amountInputRef.current) {
      setTimeout(() => amountInputRef.current?.focus(), 100);
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'barcode' && barcodeInputRef.current) {
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
    }
  }, [mode]);

  const handleBarcodeInput = async (e) => {
    const value = e.target.value;
    setBarcodeInput(value);
    
    if (value.includes('BORGESE-CARD-')) {
      const match = value.match(/BORGESE-CARD-([a-zA-Z0-9-]+)/);
      if (match) {
        setBarcodeInput('');
        await processQRCode(match[1]);
      }
    }
  };

  const handleBarcodeKeyDown = async (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      e.preventDefault();
      const cardCode = barcodeInput.trim().toUpperCase();
      setBarcodeInput('');
      
      try {
        const response = await axios.get(
          `${API}/admin/physical-cards/by-code/${cardCode}`,
          getAuthHeaders()
        );
        if (response.data && response.data.user_id) {
          await fetchCustomerByUserId(response.data.user_id);
          setMode('amount');
        } else {
          toast.error('Carta non attivata');
        }
      } catch (error) {
        if (barcodeInput.includes('BORGESE-CARD-')) {
          const match = barcodeInput.match(/BORGESE-CARD-([a-zA-Z0-9-]+)/);
          if (match) await processQRCode(match[1]);
        } else {
          toast.error('Carta non trovata');
        }
      }
    }
  };

  const fetchCustomerByUserId = async (userId) => {
    try {
      const [userRes, cardRes] = await Promise.all([
        axios.get(`${API}/admin/customers`, getAuthHeaders()),
        axios.get(`${API}/cards/${userId}`, getAuthHeaders())
      ]);
      const customer = userRes.data.find(c => c.id === userId);
      if (customer) {
        setSelectedCustomer(customer);
        setCustomerCard(cardRes.data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento cliente');
    }
  };

  const onScanSuccess = async (decodedText) => {
    if (scannerRef.current) {
      scannerRef.current.pause();
    }
    const cardIdMatch = decodedText.match(/BORGESE-CARD-([a-zA-Z0-9-]+)/);
    if (cardIdMatch) {
      await processQRCode(cardIdMatch[1]);
    } else {
      toast.error('QR code non valido');
      if (scannerRef.current) scannerRef.current.resume();
    }
  };

  const onScanFailure = () => {};

  const processQRCode = async (cardId) => {
    try {
      const response = await axios.get(`${API}/cards/by-card-id/${cardId}`, getAuthHeaders());
      setScannedCardId(cardId);
      setCustomerCard(response.data);
      
      const customersRes = await axios.get(`${API}/admin/customers`, getAuthHeaders());
      const customer = customersRes.data.find(c => c.id === response.data.user_id);
      if (customer) {
        setSelectedCustomer(customer);
        setMode('amount');
      }
    } catch (error) {
      toast.error('Carta non trovata nel sistema');
      if (scannerRef.current) scannerRef.current.resume();
    }
  };

  const handleSelectCustomer = async (customer) => {
    try {
      const cardRes = await axios.get(`${API}/cards/${customer.id}`, getAuthHeaders());
      setSelectedCustomer(customer);
      setCustomerCard(cardRes.data);
      setMode('amount');
    } catch (error) {
      toast.error('Errore nel caricamento della carta');
    }
  };

  const handleAddPurchase = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/admin/add-purchase`,
        { user_id: selectedCustomer.id, amount: parseFloat(amount) },
        getAuthHeaders()
      );
      
      setLastTransaction({
        customer: selectedCustomer.full_name,
        amount: parseFloat(amount),
        points_earned: response.data.points_earned,
        new_points: response.data.new_points,
        discount_earned: response.data.discount_earned
      });
      
      setMode('success');
    } catch (error) {
      toast.error('Errore nella registrazione');
    } finally {
      setProcessing(false);
    }
  };

  const resetScanner = () => {
    setMode('select');
    setScannedCardId(null);
    setSelectedCustomer(null);
    setCustomerCard(null);
    setAmount('');
    setLastTransaction(null);
    setSearchQuery('');
    setBarcodeInput('');
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={`min-h-screen ${t.bg}`}>
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${t.glow1} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-1/4 right-1/4 w-72 h-72 ${t.glow2} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className={`relative z-10 border-b ${t.header}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <Link to="/admin" className={`flex items-center gap-2 ${t.navLink} transition-colors`}>
              <Home size={20} />
              <span className="hidden sm:inline text-sm">Dashboard</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4A5D23] to-[#2E3A16] flex items-center justify-center shadow-lg shadow-[#4A5D23]/20 animate-pulse">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${t.text}`}>Scanner Cassa</h1>
                <p className="text-sm text-[#7CB342]">Aggiungi punti velocemente</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={onLogout} className={`p-2 rounded-lg ${t.textSecondary} hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} transition-colors`}>
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Mode Selection */}
        {mode === 'select' && (
          <div className="space-y-8 animate-fade-in">
            <div className="text-center mb-12">
              <h2 className={`text-3xl font-bold ${t.text} mb-3`}>Scegli come identificare il cliente</h2>
              <p className={t.textSecondary}>Seleziona il metodo più comodo</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Camera QR */}
              <button
                onClick={() => setMode('qr')}
                className={`group rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#4A5D23]/30 to-[#4A5D23]/10 border-[#4A5D23]/30 hover:border-[#4A5D23]' : 'bg-gradient-to-br from-[#4A5D23]/10 to-[#4A5D23]/5 border-[#4A5D23]/20 hover:border-[#4A5D23]/50'} border p-8 text-center hover:shadow-lg hover:shadow-[#4A5D23]/20 transition-all hover:-translate-y-2`}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#4A5D23] to-[#2E3A16] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Camera className="w-10 h-10 text-white" />
                </div>
                <h3 className={`text-xl font-bold ${t.text} mb-2`}>Fotocamera</h3>
                <p className={`${t.textSecondary} text-sm`}>Scansiona il QR code del cliente con la fotocamera</p>
              </button>

              {/* USB Barcode */}
              <button
                onClick={() => setMode('barcode')}
                className={`group rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#C5A059]/30 to-[#C5A059]/10 border-[#C5A059]/30 hover:border-[#C5A059]' : 'bg-gradient-to-br from-[#C5A059]/10 to-[#C5A059]/5 border-[#C5A059]/20 hover:border-[#C5A059]/50'} border p-8 text-center hover:shadow-lg hover:shadow-[#C5A059]/20 transition-all hover:-translate-y-2`}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#8B6914] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Barcode className="w-10 h-10 text-white" />
                </div>
                <h3 className={`text-xl font-bold ${t.text} mb-2`}>Lettore USB</h3>
                <p className={`${t.textSecondary} text-sm`}>Usa il lettore barcode USB per scansionare</p>
              </button>

              {/* Manual Search */}
              <button
                onClick={() => setMode('search')}
                className={`group rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#5D1822]/30 to-[#5D1822]/10 border-[#5D1822]/30 hover:border-[#5D1822]' : 'bg-gradient-to-br from-[#5D1822]/10 to-[#5D1822]/5 border-[#5D1822]/20 hover:border-[#5D1822]/50'} border p-8 text-center hover:shadow-lg hover:shadow-[#5D1822]/20 transition-all hover:-translate-y-2`}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Search className="w-10 h-10 text-white" />
                </div>
                <h3 className={`text-xl font-bold ${t.text} mb-2`}>Cerca Cliente</h3>
                <p className={`${t.textSecondary} text-sm`}>Trova manualmente il cliente per nome</p>
              </button>
            </div>
          </div>
        )}

        {/* QR Scanner Mode */}
        {mode === 'qr' && (
          <div className="space-y-6 animate-fade-in">
            <button onClick={resetScanner} className={`flex items-center gap-2 ${t.textSecondary} hover:${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
              <ArrowLeft size={20} />
              Indietro
            </button>
            
            <div className={`rounded-3xl ${t.card} border p-6 sm:p-8 overflow-hidden`}>
              <h3 className={`text-xl font-bold ${t.text} mb-6 flex items-center gap-3`}>
                <Camera className="w-6 h-6 text-[#4A5D23]" />
                Inquadra il QR Code
              </h3>
              <div id="qr-scanner-quick" className="rounded-2xl overflow-hidden"></div>
              
              {/* Messaggio di aiuto per problemi webcam */}
              <div className={`mt-6 p-4 rounded-xl ${isDark ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'} border`}>
                <p className={`text-sm ${isDark ? 'text-orange-300' : 'text-orange-700'} mb-3`}>
                  <strong>⚠️ La webcam non funziona?</strong>
                </p>
                <p className={`text-xs ${isDark ? 'text-orange-300/80' : 'text-orange-600'} mb-3`}>
                  La fotocamera richiede una connessione sicura (HTTPS). Su PC usa una delle alternative:
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setMode('search')}
                    className="flex-1 px-4 py-2 bg-[#5D1822] text-white rounded-lg text-sm font-medium hover:bg-[#4A1219] transition-colors flex items-center justify-center gap-2"
                  >
                    <Search size={16} />
                    Cerca Cliente
                  </button>
                  <button
                    onClick={() => setMode('barcode')}
                    className="flex-1 px-4 py-2 bg-[#C5A059] text-white rounded-lg text-sm font-medium hover:bg-[#A88B4A] transition-colors flex items-center justify-center gap-2"
                  >
                    <Barcode size={16} />
                    Lettore USB
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Barcode Scanner Mode */}
        {mode === 'barcode' && (
          <div className="space-y-6 animate-fade-in">
            <button onClick={resetScanner} className={`flex items-center gap-2 ${t.textSecondary} hover:${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
              <ArrowLeft size={20} />
              Indietro
            </button>
            
            <div className={`rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#C5A059]/20 to-[#C5A059]/5 border-[#C5A059]/30' : 'bg-gradient-to-br from-[#C5A059]/10 to-[#C5A059]/5 border-[#C5A059]/20'} border p-8 sm:p-12 text-center`}>
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 sm:mb-8 rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#8B6914] flex items-center justify-center animate-pulse">
                <Barcode className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </div>
              <h3 className={`text-xl sm:text-2xl font-bold ${t.text} mb-4`}>Lettore Barcode USB Attivo</h3>
              <p className={`${t.textSecondary} mb-6 sm:mb-8 text-sm sm:text-base`}>Scansiona il codice a barre della carta cliente</p>
              
              <input
                ref={barcodeInputRef}
                type="text"
                value={barcodeInput}
                onChange={handleBarcodeInput}
                onKeyDown={handleBarcodeKeyDown}
                placeholder="In attesa della scansione..."
                className={`w-full max-w-md mx-auto px-4 sm:px-6 py-3 sm:py-4 ${isDark ? 'bg-black/30 border-[#C5A059]/50 text-white placeholder-white/30' : 'bg-white border-[#C5A059]/50 text-gray-900 placeholder-gray-400'} border-2 rounded-2xl text-center text-lg sm:text-xl font-mono focus:outline-none focus:border-[#C5A059]`}
                autoFocus
              />
              
              <p className={`${t.textMuted} text-xs sm:text-sm mt-4`}>
                Il codice apparirà automaticamente quando scannerizzi
              </p>
            </div>
          </div>
        )}

        {/* Manual Search Mode */}
        {mode === 'search' && (
          <div className="space-y-6 animate-fade-in">
            <button onClick={resetScanner} className={`flex items-center gap-2 ${t.textSecondary} hover:${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
              <ArrowLeft size={20} />
              Indietro
            </button>
            
            <div className={`rounded-3xl ${t.card} border p-8`}>
              <h3 className={`text-xl font-bold ${t.text} mb-6 flex items-center gap-3`}>
                <Users className="w-6 h-6 text-[#5D1822]" />
                Cerca Cliente
              </h3>
              
              <div className="relative mb-6">
                <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${t.textMuted} w-5 h-5`} />
                <input
                  type="text"
                  placeholder="Nome o email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-12 pr-4 py-4 ${t.input} border rounded-xl focus:outline-none focus:border-[#5D1822]/50`}
                  autoFocus
                />
              </div>
              
              <div className="max-h-80 overflow-y-auto space-y-2">
                {filteredCustomers.map(customer => (
                  <button
                    key={customer.id}
                    onClick={() => handleSelectCustomer(customer)}
                    className={`w-full p-4 rounded-xl ${t.card} border hover:${isDark ? 'bg-white/10' : 'bg-gray-50'} hover:border-[#5D1822]/50 transition-all text-left flex items-center gap-4`}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center text-white font-bold">
                      {customer.full_name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className={`${t.text} font-semibold`}>{customer.full_name}</p>
                      <p className={`${t.textSecondary} text-sm`}>{customer.email}</p>
                    </div>
                  </button>
                ))}
                {filteredCustomers.length === 0 && searchQuery && (
                  <p className={`text-center ${t.textMuted} py-8`}>Nessun cliente trovato</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Amount Entry Mode */}
        {mode === 'amount' && selectedCustomer && (
          <div className="space-y-6 animate-fade-in">
            <button onClick={resetScanner} className={`flex items-center gap-2 ${t.textSecondary} hover:${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
              <ArrowLeft size={20} />
              Indietro
            </button>
            
            {/* Customer Card */}
            <div className={`rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#5D1822]/40 to-[#3A0F15]/40 border-[#5D1822]/30' : 'bg-gradient-to-br from-[#5D1822]/10 to-[#3A0F15]/10 border-[#5D1822]/20'} border p-8`}>
              <div className="flex items-center gap-6 mb-6">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#8B6914] flex items-center justify-center text-white text-3xl font-bold">
                  {selectedCustomer.full_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className={`text-2xl font-bold ${t.text}`}>{selectedCustomer.full_name}</h3>
                  <p className={t.textSecondary}>{selectedCustomer.email}</p>
                </div>
              </div>
              
              {customerCard && (
                <div className="grid grid-cols-2 gap-4">
                  <div className={`rounded-xl ${isDark ? 'bg-white/10' : 'bg-white/50'} p-4 text-center`}>
                    <p className={`${t.textSecondary} text-sm mb-1`}>Punti Attuali</p>
                    <p className="text-3xl font-bold text-[#C5A059]">{customerCard.current_points}</p>
                  </div>
                  <div className={`rounded-xl ${isDark ? 'bg-white/10' : 'bg-white/50'} p-4 text-center`}>
                    <p className={`${t.textSecondary} text-sm mb-1`}>Totale Guadagnati</p>
                    <p className={`text-3xl font-bold ${t.text}`}>{customerCard.total_points_earned}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Amount Input */}
            <div className={`rounded-3xl ${t.card} border p-8`}>
              <label className={`block ${t.textSecondary} text-sm mb-4`}>Importo Spesa</label>
              <div className="relative mb-6">
                <Euro className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C5A059] w-8 h-8" />
                <input
                  ref={amountInputRef}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-16 pr-4 py-6 ${t.input} border-2 rounded-2xl text-4xl font-bold text-center focus:outline-none focus:border-[#4A5D23]/50`}
                  step="0.01"
                  min="0"
                />
              </div>
              
              <button
                onClick={handleAddPurchase}
                disabled={processing || !amount}
                className="w-full py-5 bg-gradient-to-r from-[#4A5D23] to-[#2E3A16] text-white rounded-2xl font-bold text-xl flex items-center justify-center gap-3 hover:shadow-lg hover:shadow-[#4A5D23]/30 transition-all disabled:opacity-50"
              >
                {processing ? (
                  'Elaborazione...'
                ) : (
                  <>
                    <Sparkles size={24} />
                    Conferma e Aggiungi Punti
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Success Mode */}
        {mode === 'success' && lastTransaction && (
          <div className="animate-fade-in text-center">
            <div className={`rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#4A5D23]/40 to-[#2E3A16]/40 border-[#4A5D23]/50' : 'bg-gradient-to-br from-[#4A5D23]/10 to-[#2E3A16]/10 border-[#4A5D23]/30'} border p-12`}>
              <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-[#4A5D23] flex items-center justify-center">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              
              <h2 className={`text-3xl font-bold ${t.text} mb-2`}>Transazione Completata!</h2>
              <p className={`${t.textSecondary} mb-8`}>{lastTransaction.customer}</p>
              
              <div className="grid grid-cols-2 gap-6 mb-8 max-w-md mx-auto">
                <div className={`rounded-2xl ${isDark ? 'bg-white/10' : 'bg-white/50'} p-6`}>
                  <p className={`${t.textSecondary} text-sm mb-2`}>Spesa</p>
                  <p className={`text-3xl font-bold ${t.text}`}>€{lastTransaction.amount.toFixed(2)}</p>
                </div>
                <div className="rounded-2xl bg-[#C5A059]/20 p-6">
                  <p className="text-[#C5A059] text-sm mb-2">Punti Guadagnati</p>
                  <p className="text-3xl font-bold text-[#C5A059]">+{lastTransaction.points_earned}</p>
                </div>
              </div>
              
              {lastTransaction.discount_earned && (
                <div className="rounded-2xl bg-[#4A5D23]/30 border border-[#4A5D23]/50 p-6 mb-8 max-w-md mx-auto">
                  <div className="flex items-center justify-center gap-3">
                    <Gift className="w-8 h-8 text-[#7CB342]" />
                    <p className="text-xl font-bold text-[#7CB342]">🎉 Sconto €{Number(lastTransaction.discount_earned).toFixed(2)} sbloccato!</p>
                  </div>
                </div>
              )}
              
              <p className={`${t.textSecondary} mb-8`}>
                Nuovo saldo: <span className={`${t.text} font-bold`}>{lastTransaction.new_points} punti</span>
              </p>
              
              <button
                onClick={resetScanner}
                className="px-8 py-4 bg-gradient-to-r from-[#C5A059] to-[#8B6914] text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-3 mx-auto hover:shadow-lg hover:shadow-[#C5A059]/30 transition-all"
              >
                <RotateCcw size={20} />
                Nuova Transazione
              </button>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
